import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { SEOHead } from '../components/seo/SEOHead';
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from '../components/ui/accordion';
import { ArrowRight, CheckCircle, AlertCircle, FileText, Shield, Clock, Users } from 'lucide-react';

const faqs = [
  {
    question: "What is a Record Suspension?",
    answer: "A Record Suspension (formerly known as a Pardon) is a status granted by the Parole Board of Canada that allows individuals with a criminal record to have their record kept separate and apart from other criminal records. This means the record will not appear on standard criminal background checks."
  },
  {
    question: "How long do I have to wait before applying?",
    answer: "The waiting period depends on the type of offence. For summary offences, you must wait 5 years after completing all sentences. For indictable offences, you must wait 10 years. The waiting period begins after all fines, probation, and sentences have been fully completed."
  },
  {
    question: "What documents are required?",
    answer: "Required documents typically include: certified criminal record from RCMP, court information for each conviction, local police records check, proof of citizenship or immigration status, and character references. Our team will guide you through the specific requirements for your case."
  },
  {
    question: "How long does the process take?",
    answer: "The Parole Board of Canada currently processes applications within 6-12 months for summary offences and 12-24 months for indictable offences. Processing times vary based on the complexity of your case and current application volumes."
  },
  {
    question: "Is approval guaranteed?",
    answer: "No application outcome is guaranteed. The Parole Board of Canada makes all approval decisions based on statutory criteria. Canadian Document Services provides administrative preparation services only and does not influence government decisions."
  },
  {
    question: "What if I am not eligible?",
    answer: "If our eligibility review determines you do not meet the statutory criteria, we will provide clear guidance on what requirements need to be met and when you may become eligible to apply."
  }
];

const eligibilityRequirements = [
  "Completed all sentences (including fines, probation)",
  "Waiting period satisfied (5 years summary, 10 years indictable)",
  "No new criminal convictions during waiting period",
  "Good conduct demonstrated"
];

const processTimeline = [
  { step: "Initial Assessment", duration: "Same Day", description: "Complete our eligibility assessment form to begin the review process." },
  { step: "Document Collection", duration: "2-6 Weeks", description: "Gather required documents including RCMP records, court documents, and police checks." },
  { step: "Application Preparation", duration: "1-2 Weeks", description: "Complete application package assembled and reviewed for compliance." },
  { step: "Submission", duration: "Upon Completion", description: "Application formally submitted to the Parole Board of Canada." },
  { step: "Processing", duration: "6-24 Months", description: "Parole Board reviews application and issues decision." }
];

// FAQ data for schema
const faqSchemaData = faqs.map(f => ({
  question: f.question,
  answer: f.answer,
}));

export default function RecordSuspensions() {
  return (
    <>
      <SEOHead 
        page="recordSuspensions" 
        faqSchema={faqSchemaData}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Record Suspension', url: '/record-suspensions' },
        ]}
      />
      
      <div data-testid="record-suspensions-page">
        {/* Hero Section */}
        <section className="bg-[hsl(215,50%,23%)] text-white py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="max-w-3xl">
              <p className="text-white/70 text-sm uppercase tracking-wide mb-2">Criminal Records Act</p>
            <h1 className="text-3xl md:text-4xl font-bold mb-4 text-white">Record Suspension</h1>
            <p className="text-white/80 text-lg mb-6">
              Administrative preparation services for Record Suspension applications 
              under the Criminal Records Act. Formal eligibility review conducted 
              prior to any filing.
            </p>
            <Link to="/eligibility">
              <Button className="bg-white text-[hsl(215,50%,23%)] hover:bg-gray-100 font-semibold px-6 py-3 rounded-sm" data-testid="rs-cta-btn">
                Check Eligibility
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="gov-section">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="gov-section-title">What is a Record Suspension?</h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  A Record Suspension (formerly known as a Pardon) allows people who were 
                  convicted of a criminal offence, but have completed their sentence and 
                  demonstrated they are law-abiding citizens, to have their criminal record 
                  kept separate and apart from other criminal records.
                </p>
                <p>
                  Once granted, the record will not be disclosed on most criminal background 
                  checks, which can help with employment, education, and travel opportunities.
                </p>
              </div>
            </div>
            
            <div className="gov-panel">
              <h3 className="font-bold text-[hsl(215,50%,23%)] mb-4 text-lg">Eligibility Requirements</h3>
              <div className="space-y-3">
                {eligibilityRequirements.map((req, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{req}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="gov-section-alt">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="gov-section-title text-center mb-12">Application Timeline</h2>
          
          <div className="max-w-3xl mx-auto space-y-4">
            {processTimeline.map((item, index) => (
              <div 
                key={index} 
                className="gov-panel flex items-start gap-4"
                data-testid={`timeline-step-${index + 1}`}
              >
                <div className="w-10 h-10 rounded-full bg-[hsl(215,50%,23%)] text-white flex items-center justify-center font-bold flex-shrink-0">
                  {index + 1}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-bold text-[hsl(215,50%,23%)]">{item.step}</h3>
                    <span className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-600">{item.duration}</span>
                  </div>
                  <p className="text-gray-600 text-sm">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-6">
            <div className="flex items-start gap-4">
              <AlertCircle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-amber-800 mb-2">Important Notice</h3>
                <p className="text-amber-700 text-sm">
                  Canadian Document Services Inc. is a private administrative document 
                  preparation company. We are not affiliated with the Government of Canada 
                  or the Parole Board of Canada. All approval decisions are made exclusively 
                  by the appropriate government authority. We do not guarantee any specific 
                  outcome.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="gov-section" data-testid="faq-section">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="gov-section-title text-center mb-12">Frequently Asked Questions</h2>
          
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-2">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`faq-${index}`}
                  className="gov-panel border border-gray-200"
                >
                  <AccordionTrigger className="text-left text-[hsl(215,50%,23%)] hover:no-underline px-0 font-semibold">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 pt-2">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="gov-section-alt">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <FileText className="h-12 w-12 text-[hsl(215,50%,23%)] mx-auto mb-6" />
          <h2 className="gov-section-title">Ready to Begin?</h2>
          <p className="gov-subtitle mb-8">
            Start with a free eligibility assessment to determine if you qualify 
            for a Record Suspension.
          </p>
          <Link to="/eligibility">
            <Button className="gov-btn-primary" data-testid="rs-bottom-cta-btn">
              Start Eligibility Assessment
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Trust & Compliance Section */}
      <section className="gov-section bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="gov-section-title text-center mb-8">Trust & Compliance</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6">
              <Shield className="h-8 w-8 text-[hsl(215,50%,23%)] mx-auto mb-4" />
              <h3 className="font-bold text-[hsl(215,50%,23%)] mb-2">Aligned with Criminal Records Act</h3>
              <p className="text-gray-600 text-sm">All preparation follows current statutory requirements and Parole Board guidelines.</p>
            </div>
            <div className="text-center p-6">
              <Clock className="h-8 w-8 text-[hsl(215,50%,23%)] mx-auto mb-4" />
              <h3 className="font-bold text-[hsl(215,50%,23%)] mb-2">Transparent Process</h3>
              <p className="text-gray-600 text-sm">Clear timelines and fee disclosure before any work begins on your application.</p>
            </div>
            <div className="text-center p-6">
              <Users className="h-8 w-8 text-[hsl(215,50%,23%)] mx-auto mb-4" />
              <h3 className="font-bold text-[hsl(215,50%,23%)] mb-2">Professional Review</h3>
              <p className="text-gray-600 text-sm">Every application is reviewed for completeness before submission to the Parole Board.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Internal Links */}
      <section className="gov-section-alt">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="text-lg font-bold text-[hsl(215,50%,23%)] mb-4">Related Services</h2>
          <div className="flex flex-wrap gap-4">
            <Link to="/us-entry-waivers" className="text-[hsl(210,80%,40%)] hover:underline text-sm">
              U.S. Entry Waivers →
            </Link>
            <Link to="/international-filings" className="text-[hsl(210,80%,40%)] hover:underline text-sm">
              International Filings →
            </Link>
            <Link to="/contact" className="text-[hsl(210,80%,40%)] hover:underline text-sm">
              Contact Us →
            </Link>
          </div>
        </div>
      </section>
      </div>
    </>
  );
}
